/**
 * Model Icon Mapper
 *
 * Maps module IDs and their variants to model display names and icon paths.
 * Handles edge cases for variants, submodels, quality levels, and imported images.
 *
 * ICON SOURCES:
 * - /agent-icons/ - LobeHub MIT-licensed icons (PNG) for common AI models
 * - /model-icons/ - Custom icons (SVG/PNG) for app-specific models and states
 *
 * RULE: When adding a new model, first check if an icon exists in /agent-icons/
 * (from LobeHub). Use icon-only versions (e.g., "flux.png" not "flux-text.png").
 * If no model icon exists after that search, fall back to the GlowGhost mark.
 */

import { getImageModel } from '../config/imageModels';
import { VIDEO_MODELS } from '../config/videoModels';
import { GLOWGHOST_MARK_SRC } from '../config/brandAssets';

/**
 * Icons available in /agent-icons/ from LobeHub (icon-only PNGs)
 * These are MIT licensed and can be used freely.
 * See: https://github.com/lobehub/lobe-icons
 */
const AGENT_ICONS = new Set([
  'flux', 'qwen', 'fal', 'stability', 'openai', 'anthropic', 'claude',
  'dalle', 'midjourney', 'huggingface', 'replicate', 'ideogram', 'recraft',
  'runway', 'pika', 'suno', 'gemini', 'google', 'meta', 'llama', 'mistral',
  'deepseek', 'minimax', 'cohere', 'perplexity', 'together', 'groq', 'fireworks',
  'cerebras', 'anyscale', 'modal', 'banana', 'baseten', 'lepton', 'octoai',
  'bytedance', 'elevenlabs',
  'topazlabs', 'grok', 'kling', 'lightricks', 'hunyuan', 'microsoft', 'tripo',
  'krea'
]);

/**
 * Custom icons in /model-icons/ for app-specific states and models with official branding
 * 
 * RULE: Only add icons here if:
 * 1. It's an app state (imported, edited)
 * 2. The model has an official icon from their website (e.g., birefnet from birefnet.top)
 * 
 * For models without official branding, resolve generic-ai to the GlowGhost mark
 * instead of inventing fake brand icons.
 */
const CUSTOM_ICONS = {
  // App states (not models)
  'imported': '/model-icons/imported.svg',
  'edited': '/model-icons/edited.svg',
  'generic-ai': GLOWGHOST_MARK_SRC,
  
  // View-based operations (non-AI adjustments)
  'refine': '/model-icons/refine.svg',
  'transform': '/model-icons/transform.svg',
  
  // Models with official icons from their websites
  'birefnet': '/model-icons/birefnet.svg',  // Official icon from birefnet.top
  'briaai': '/model-icons/briaai.svg',  // Official BRIA AI provider icon via LobeHub

  // 3D generation model assets.
  'rodin': '/model-icons/rodin.png',
  'meshy': '/model-icons/meshy.png',
};

// =============================================================================
// Legacy utility model option lists.
//
// Primary generation selectors are derived from the model repository. These
// entries remain for utility surfaces that have not moved into repository-backed
// model surfaces yet.
// =============================================================================

export const MODEL_OPTIONS = {
  /**
   * Upscale - Quality/Model selector
   * Used in: UpscaleControls.jsx
   */
  upscaleQuality: [
    { value: 'seedvr', label: 'Fast', modelName: 'SeedVR2', iconKey: 'bytedance' },
    { value: 'clarity', label: 'Standard', modelName: 'Clarity Upscaler', iconKey: 'fal' },
    { value: 'topaz', label: 'Premium', modelName: 'Topaz', iconKey: 'topazlabs' },
    { value: 'ideogram', label: 'Creative', modelName: 'Ideogram Upscale', iconKey: 'ideogram' }
  ],

  /**
   * Batch Upscale - Quality/Model selector for batch processing
   * Used in: BatchUpscaleModelSelector.jsx (Settings), batchActionRegistry.jsx
   * 
   * Tiers:
   * - Fast: SeedVR2 (best speed/cost balance)
   * - Standard: Clarity with full quality
   * - Premium: Topaz (highest quality)
   */
  batchUpscaleQuality: [
    { value: 'fast', label: 'Fast', modelName: 'SeedVR2', iconKey: 'bytedance' },
    { value: 'standard', label: 'Standard', modelName: 'Clarity Upscaler', iconKey: 'fal' },
    { value: 'premium', label: 'Premium', modelName: 'Topaz', iconKey: 'topazlabs' }
  ],

  /**
   * Batch Remove Background - Quality/Model selector for batch processing
   * Used in: BatchSettingsSection.jsx (Settings), batchActionRegistry.jsx
   * 
   * Tiers:
   * - Fast: BiRefNet Light (faster, lower quality)
   * - Standard: BiRefNet General Heavy (balanced)
   * - Premium: BiRefNet with high resolution
   */
  batchRemoveBackgroundQuality: [
    { value: 'fast', label: 'Fast', modelName: 'BiRefNet Light', iconKey: 'birefnet' },
    { value: 'standard', label: 'Standard', modelName: 'BiRefNet', iconKey: 'birefnet' },
    { value: 'premium', label: 'Premium', modelName: 'BiRefNet HD', iconKey: 'birefnet' }
  ],

  /**
   * Expand Canvas - Quality/Model selector
   * Used in: ExpandCanvasControls.jsx, ExpandHandlesOptions.jsx
   */
  expandCanvasQuality: [
    { value: 'fast', label: 'Fast' },
    { value: 'standard', label: 'Standard' }
  ],

  /**
   * Remove Background - Model selector
   * Used in: RemoveBackgroundControls.jsx
   */
  removeBackgroundModel: [
    { value: 'standard', label: 'Standard', modelName: 'General Use - Light', iconKey: 'birefnet' },
    { value: 'premium', label: 'Premium', modelName: 'General Use - Heavy', iconKey: 'birefnet' },
    { value: 'portrait', label: 'Portrait', modelName: 'BiRefNet Portrait', iconKey: 'birefnet' },
    { value: 'matting', label: 'Matting', modelName: 'BiRefNet Matting', iconKey: 'birefnet' }
  ]
};

function toReadableModelName(value) {
  if (!value || typeof value !== 'string') return null;

  return value
    .replace(/^fal-ai\//, '')
    .replace(/[-_/]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

function normalizeVideoModelKey(modelKey) {
  if (!modelKey || typeof modelKey !== 'string') return null;

  const trimmedModelKey = modelKey.trim();
  if (!trimmedModelKey) return null;

  if (VIDEO_MODELS.some((candidate) => candidate.key === trimmedModelKey)) {
    return trimmedModelKey;
  }

  const normalizedModelKey = trimmedModelKey.toLowerCase();
  if (normalizedModelKey.includes('kling-video/v3')) return 'klingV3';
  if (normalizedModelKey.includes('kling-video/o3')) return 'klingO3';
  if (normalizedModelKey.includes('kling-video/v1.5')) return 'klingV15';
  // Both, not one: persisted rows from the 2.3 era still have to normalize.
  if (normalizedModelKey.includes('ltx-2.3')) return 'ltx';
  if (normalizedModelKey.includes('ltx-2.5')) return 'ltx';
  if (normalizedModelKey.includes('veo3.1')) return 'veo31';

  return null;
}

function getVideoGenerationModelInfo(generationParams = {}) {
  const explicitModelKey = generationParams.model
    || generationParams.moduleParams?.model
    || generationParams.moduleParams?.modelKey
    || generationParams.runRecord?.execution?.modelKey
    || null;
  const normalizedModelKey = normalizeVideoModelKey(explicitModelKey);

  if (normalizedModelKey) {
    const videoModel = VIDEO_MODELS.find((candidate) => candidate.key === normalizedModelKey);
    if (videoModel) {
      return {
        modelName: normalizedModelKey === 'ltx' ? 'LTX Video' : videoModel.label,
        iconKey: videoModel.iconKey,
      };
    }
  }

  if (explicitModelKey) {
    return {
      modelName: toReadableModelName(explicitModelKey) || 'Video Generation',
      iconKey: 'generic-ai',
    };
  }

  return {
    modelName: 'LTX Video',
    iconKey: 'lightricks',
  };
}

function getGraphAttributionModelInfo(generationParams = {}) {
  const graphAttribution = generationParams.graphAttribution;
  if (!graphAttribution || typeof graphAttribution !== 'object') return null;

  if (graphAttribution.displayMode === 'edited') {
    return {
      modelName: 'Edited',
      iconKey: 'edited'
    };
  }

  const primaryModel = graphAttribution.primaryModel || graphAttribution.models?.[0] || null;
  const modelKey = primaryModel?.generationParams?.module
    || primaryModel?.generationParams?.model
    || primaryModel?.key
    || graphAttribution.modelKeys?.[0]
    || null;
  const imageModel = getImageModel(modelKey);
  if (imageModel) {
    return {
      modelName: imageModel.label,
      iconKey: imageModel.iconKey
    };
  }

  if (primaryModel?.modelName && primaryModel?.iconKey) {
    return {
      modelName: primaryModel.modelName,
      iconKey: primaryModel.iconKey
    };
  }

  return null;
}

/**
 * Get model information (display name and icon) for a given image
 * 
 * @param {Object} generationParams - The generationParams object from an image
 * @returns {Object} { modelName, iconKey } or null if no model info available
 */
export function getModelInfo(generationParams) {
  if (!generationParams) {
    return null;
  }

  const { module, variant, submodel, quality, model } = generationParams;

  const graphAttributionModelInfo = getGraphAttributionModelInfo(generationParams);
  if (graphAttributionModelInfo) return graphAttributionModelInfo;

  // Handle imported images
  if (module === 'imported') {
    return {
      modelName: 'Imported Image',
      iconKey: 'imported'
    };
  }

  // More Like This uses Flux Kontext Max through a continuation-only module key.
  // Keep the completed tile badge aligned with the in-flight generation badge.
  if (module === 'kontext-max-variation' || model === 'kontext-max-variation') {
    return {
      modelName: 'Variation (Kontext Max)',
      iconKey: 'flux'
    };
  }

  // Image model registry lookup (covers generate model keys and edit keys)
  const imageModel = getImageModel(module);
  if (imageModel) return { modelName: imageModel.label, iconKey: imageModel.iconKey };
  const explicitModel = getImageModel(model);
  if (explicitModel) return { modelName: explicitModel.label, iconKey: explicitModel.iconKey };
  // Legacy alias: flux2Pro (camelCase module ID) → flux-2-pro (registry key)
  if (module === 'flux2Pro') {
    const m = getImageModel('flux-2-pro');
    return m ? { modelName: m.label, iconKey: m.iconKey } : null;
  }

  // Handle each module type
  switch (module) {
    case 'addObject':
    case 'add-object':
      // Check the model field to determine which variant
      if (model === 'nano-banana-2-flash') {
        return {
          modelName: 'Nano Banana 2',
          iconKey: 'gemini'
        };
      }
      if (model === 'nano-banana-2') {
        return {
          modelName: 'Nano Banana Pro',
          iconKey: 'gemini'
        };
      }
      // Default to Flux 2 Edit
      return {
        modelName: 'Flux 2 Edit',
        iconKey: 'flux'
      };

    // ===== EDIT MODULES =====
    case 'generativeFill': {
      // Model name and icon are now passed dynamically from editModels.js
      // via setGenerativeLoading. This is the fallback for other display contexts.
      if (variant === 'precise') {
        return { modelName: 'Inpaint', iconKey: 'qwen' };
      }
      return { modelName: 'Smart Edit', iconKey: 'gemini' };
    }

    case 'removeObject':
      // Check variant
      if (variant === 'precise') {
        return {
          modelName: 'Finegrain Eraser',
          iconKey: 'generic-ai'  // No official Finegrain icon available
        };
      }
      // Default to smart mode
      return {
        modelName: 'Object Remover',
        iconKey: 'qwen'
      };

    case 'expandCanvas':
      // Check submodel
      if (submodel === 'bria') {
        return {
          modelName: 'Bria Expand',
          iconKey: 'briaai'
        };
      }
      // Default to FAL Outpaint
      return {
        modelName: 'FAL Outpaint',
        iconKey: 'fal'  // Use FAL's official icon
      };

    case 'smartResize':
      return {
        modelName: 'Smart Resize',
        iconKey: 'fal'
      };

    case 'imageRelightExplorer':
      return {
        modelName: 'IC-Light v2',
        iconKey: 'fal'
      };

    case 'upscale':
      // Check quality level
      if (quality === 'premium') {
        return {
          modelName: 'Topaz',
          iconKey: 'topazlabs'
        };
      }
      // Default to Clarity
      return {
        modelName: 'Clarity Upscaler',
        iconKey: 'fal'  // Clarity is provided via FAL
      };

    // Every module that PRODUCES video resolves through one reader, because the model key is
    // already on the row and the only thing that was missing is this branch.
    //
    // The storyboard's three video paths -- animate, extend and the sequence render -- each
    // stamp their own module name and a real `model`, but none of them appeared here, so they
    // fell through the switch and displayed the generic GlowGhost mark. The metadata was never
    // the problem; the SECOND list of "which modules make video" was, and it lived here.
    case 'textToVideo':
    case 'video-generate':
    case 'storyboardAnimate':
    case 'storyboardSequence':
    case 'videoExtend': {
      return getVideoGenerationModelInfo(generationParams);
    }

    case 'videoSmartEdit':
      if (model === 'geminiOmniFlash') {
        return { modelName: 'Gemini Omni Flash Video Edit', iconKey: 'google' };
      }
      if (model === 'happyHorse') {
        return { modelName: 'HappyHorse Video Edit', iconKey: 'fal' };
      }
      if (model === 'wan27') {
        return { modelName: 'Wan 2.7 Edit Video', iconKey: 'fal' };
      }
      return { modelName: 'Video AI Edit', iconKey: 'fal' };

    case 'videoRecast':
      return { modelName: 'MiniMax H3 Max Recast', iconKey: 'minimax' };

    // Retired module; kept so clips it made still name their model.
    case 'videoVfx':
      if (model === 'klingO1Edit') {
        return { modelName: 'Kling O3 Edit', iconKey: 'kling' };
      }
      if (model === 'veo31') {
        return { modelName: 'Veo 3.1', iconKey: 'google' };
      }
      return {
        modelName: 'Video VFX',
        iconKey: 'fal'
      };

    case 'videoRemoveBackground':
      return {
        modelName: 'Bria Video Background Removal',
        iconKey: 'briaai'
      };

    case 'audioCleanUpVoice':
      return {
        modelName: 'ElevenLabs Voice Isolation',
        iconKey: 'elevenlabs'
      };

    case 'audioSplitStems':
      return {
        modelName: 'Demucs Stem Separation',
        iconKey: 'fal'
      };

    case 'videoUpscale':
      return {
        modelName: 'Topaz Video Upscale',
        iconKey: 'topazlabs'
      };

    case '3dGenerate': {
      const threeDModel = generationParams.model;
      if (threeDModel === 'trellis') return { modelName: 'Trellis 2', iconKey: 'microsoft' };
      if (threeDModel === 'tripo3d') return { modelName: 'Tripo3D v2.5', iconKey: 'tripo' };
      if (threeDModel === 'hunyuan3d') return { modelName: 'Hunyuan3D Pro', iconKey: 'hunyuan' };
      if (threeDModel === 'meshy') return { modelName: 'Meshy', iconKey: 'meshy' };
      return { modelName: 'Hyper3D Rodin v2', iconKey: 'rodin' };
    }

    case 'removeBackground':
      return {
        modelName: 'BiRefNet',
        iconKey: 'birefnet'
      };

    // ===== NON-GENERATIVE MODULES =====
    // These don't show model icons
    case 'light':
    case 'curves':
    case 'color':
    case 'crop':
    case 'rotation':
    case 'export':
      return null;

    // ===== UNKNOWN MODULE =====
    default:
      // Derive a readable name from the module key (e.g. 'lucy-restyle' → 'Lucy Restyle')
      if (module) {
        const readable = module
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
        return {
          modelName: readable,
          iconKey: 'generic-ai'
        };
      }
      return null;
  }
}

/**
 * Get the icon path for a given icon key
 * 
 * Resolution order:
 * 1. Custom icons in /model-icons/ (for app-specific icons)
 * 2. LobeHub icons in /agent-icons/ (for common AI models)
 * 3. GlowGhost icon fallback
 * 
 * @param {string} iconKey - The icon key from getModelInfo
 * @returns {string} Path to the icon file
 */
export function getIconPath(iconKey) {
  if (!iconKey) {
    return GLOWGHOST_MARK_SRC;
  }
  
  // Check if it's a custom icon first
  if (CUSTOM_ICONS[iconKey]) {
    return CUSTOM_ICONS[iconKey];
  }
  
  // Check if it's an agent icon from LobeHub
  if (AGENT_ICONS.has(iconKey)) {
    return `/agent-icons/${iconKey}.png`;
  }
  
  return GLOWGHOST_MARK_SRC;
}

/**
 * Map action IDs to detailed action info
 * Includes model name, icon, and action description
 * 
 * Categories:
 * - AI operations: Use model icons (birefnet, fal, etc.)
 * - View operations: Use view icons (refine, transform)
 */
const ACTION_INFO = {
  // === AI Operations (with model attribution) ===
  'remove-background': {
    modelName: 'BiRefNet',
    iconKey: 'birefnet',
    actionLabel: 'Background removed',
    isAI: true
  },
  'upscale-2x': {
    modelName: 'Topaz',
    iconKey: 'topazlabs',
    actionLabel: 'Upscaled 2x',
    isAI: true
  },
  'upscale': {
    modelName: 'Clarity',
    iconKey: 'fal',
    actionLabel: 'Upscaled',
    isAI: true
  },
  
  // === View Operations (no AI, just adjustments) ===
  'refine': {
    modelName: null,  // No model - these are non-AI adjustments
    iconKey: 'refine',
    actionLabel: 'Refined',
    isAI: false
  },
  'transform': {
    modelName: null,
    iconKey: 'transform',
    actionLabel: 'Transformed',
    isAI: false
  }
};

/**
 * Get detailed info for an edit history entry
 * Returns { modelName, iconPath, actionLabel, isAI }
 */
export function getEditHistoryEntryInfo(entry) {
  const action = entry.action;
  
  // Look up from action info
  if (action && ACTION_INFO[action]) {
    const info = ACTION_INFO[action];
    return {
      modelName: info.modelName,
      iconPath: getIconPath(info.iconKey),
      actionLabel: info.actionLabel,
      isAI: info.isAI
    };
  }
  
  // Fallback for unknown actions - assume AI operation
  return {
    modelName: entry.model || entry.action || 'Unknown',
    iconPath: getIconPath('generic-ai'),
    actionLabel: entry.action || 'Edited',
    isAI: true
  };
}

/**
 * Get complete model display info for an image
 * Convenience function that combines getModelInfo and getIconPath
 * 
 * If the image has edit history, returns "Edited" with consolidated history for tooltip display.
 * Duplicate operations are consolidated into single entries (e.g., multiple refines = one "Refined" entry)
 * 
 * @param {Object} generationParams - The generationParams object from an image
 * @returns {Object|null} { modelName, iconPath, originalModel?, editHistory? } or null
 */
export function getModelDisplayInfo(generationParams) {
  // Get the original generation model info (before any edits)
  const originalModelInfo = getModelInfo(generationParams);
  
  // Check for edit history - if image has been edited, show edited icon
  if (generationParams?.editHistory?.length > 0) {
    // Consolidate duplicate operations - only show each unique operation type once
    const seenActions = new Set();
    const consolidatedHistory = [];
    
    for (const entry of generationParams.editHistory) {
      const action = entry.action;
      if (!seenActions.has(action)) {
        seenActions.add(action);
        consolidatedHistory.push(getEditHistoryEntryInfo(entry));
      }
    }
    
    return {
      modelName: 'Edited',
      iconPath: '/model-icons/edited.svg',
      // Include original model info for "Generated with X" line
      originalModel: originalModelInfo ? {
        modelName: originalModelInfo.modelName,
        iconPath: getIconPath(originalModelInfo.iconKey)
      } : null,
      // Consolidated edit history (unique operations only)
      editHistory: consolidatedHistory
    };
  }
  
  if (!originalModelInfo) {
    return null;
  }

  return {
    modelName: originalModelInfo.modelName,
    iconPath: getIconPath(originalModelInfo.iconKey)
  };
}

export default {
  getModelInfo,
  getIconPath,
  getModelDisplayInfo,
  getEditHistoryEntryInfo
};
